import { MaxParamThresholds } from './getMaxAmountOpen'

/**
 * Compute the maximum dollar amount for a debt swap without
 * dropping the health factor below `SAFE_HF`.
 *
 * A debt swap repays $X of asset A (source) and borrows $X of asset B (target).
 * The adjusted debt changes by `(bfOut - bfIn) * X` where:
 *   bfIn  = borrowFactor of the source (repaid) debt
 *   bfOut = borrowFactor of the target (new) debt
 *
 * If the target asset has a lower or equal borrowFactor, the swap always
 * improves health → returns Infinity (capped by position size upstream).
 *
 * @param borrowDiscountedCollateral  Current risk-adjusted collateral
 * @param adjustedDebt               Current risk-adjusted debt
 * @param bfIn   borrowFactor of the source debt (being repaid)
 * @param bfOut  borrowFactor of the target debt (being borrowed)
 * @returns Maximum dollar amount that can be swapped
 */
export function getMaxAmountDebtSwap(
  borrowDiscountedCollateral: number,
  adjustedDebt: number,
  bfIn: number,
  bfOut: number,
): number {
  // Swapping to a lower-risk debt always improves health
  if (bfOut <= bfIn) return Infinity

  const denominator = MaxParamThresholds.SAFE_HF * (bfOut - bfIn)
  return (
    (borrowDiscountedCollateral - MaxParamThresholds.SAFE_HF * adjustedDebt) /
    denominator
  )
}
